import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';

import IsMarkdownModeQuery, {
	type IsMarkdownModeQuery$data,
	type IsMarkdownModeQuery$variables,
} from './__generated__/IsMarkdownModeQuery.graphql.ts';
import { getIsMarkdownModeFromData } from './getIsMarkdownModeFromData.ts';

/**
 * Synchronous read of the markdown-mode flag straight from the Apollo cache.
 *
 * The same query is exposed reactively via `useIsMarkdownMode`, but Apollo's
 * `useQuery` subscription delivers cache-change notifications across one or
 * more renders later than the cache write itself. That gap matters for the
 * `.md`-suffix gesture's cancel-restore: the convert-mutation `update`
 * callback patches the cache synchronously before the dialog's `onClose`
 * fires, so a direct read here returns the post-confirm value in time for
 * the close-edge effect to discriminate confirm from cancel.
 *
 * Returns `false` when the experiment is off, the contentId is missing, or
 * nothing has populated the query for this content yet.
 */
export const readIsMarkdownModeFromCache = (contentId: string | undefined): boolean => {
	if (!contentId || !expValEquals('cc-markdown-mode', 'isEnabled', true)) {
		return false;
	}
	try {
		const cached = readQueryInterop<IsMarkdownModeQuery$data, IsMarkdownModeQuery$variables>({
			query: IsMarkdownModeQuery,
			variables: {
				contentId,
				isMarkdownModeEnabled: true,
			},
		});
		return getIsMarkdownModeFromData(cached);
	} catch {
		// Cache miss (query never ran for this contentId) — treat as not in markdown mode.
		return false;
	}
};
